import { CenteredSpinner, PageScroll } from "@Components/Page";
import { gatewayUrl } from "@Constants/gatewayUrl";
import { useUserContext } from "@Hooks/contexts/useUserContext";
import { Banner, Button, DocumentIcon, EmptyState, ErrorIcon, GroupedList, IconButton, ListRow, PageTitle, RefreshIcon, SearchField, SearchIcon, Spinner, TextButton, Toast } from "@UI";
import { createConfig } from "@Utils/createConfig";
import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { WorkflowWrapper } from "./WorkflowWrapper";
export function Templates({ onExit }) {
    const { t } = useTranslation();
    const token = useUserContext((s)=>s?.user?.meta?.access_token.token);
    const [templates, setTemplates] = useState([]);
    const [isLoadingTemplateList, setIsLoadingTemplateList] = useState(false);
    const [hasLoadError, setHasLoadError] = useState(false);
    const [error, setError] = useState(null);
    const [isLoadingTemplate, setIsLoadingTemplate] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const filteredTemplates = useMemo(()=>{
        const query = searchQuery.toLowerCase().trim();
        if (!query.length) return templates;
        return templates.filter((template)=>template.title.toLowerCase().includes(query));
    }, [
        searchQuery,
        templates
    ]);
    const refreshTemplates = async ()=>{
        setIsLoadingTemplateList(true);
        setHasLoadError(false);
        try {
            const header = createConfig(token);
            const url = `${gatewayUrl}/plugins/word/templates`;
            const { data } = await axios.get(url, header);
            setTemplates(data);
        } catch (err) {
            console.error(err);
            setHasLoadError(true);
        } finally{
            setIsLoadingTemplateList(false);
        }
    };
    useEffect(()=>{
        Office.onReady(()=>{
            refreshTemplates();
        });
    }, []);
    const openDocument = async (id)=>{
        try {
            setIsLoadingTemplate(id);
            const header = createConfig(token);
            const url = `${gatewayUrl}/plugins/word/templates/download/${id}`;
            const response = await axios.get(url, {
                ...header,
                responseType: "arraybuffer"
            });
            const base64 = window.btoa(new Uint8Array(response.data).reduce((data, byte)=>data + String.fromCharCode(byte), ""));
            await Word.run(async (context)=>{
                const newDoc = context.application.createDocument(base64);
                newDoc.open();
                await context.sync();
            });
        } catch (err) {
            console.error(err);
            setError({
                message: t("workflows.TemplateLibrary.openFailed"),
                retry: ()=>openDocument(id)
            });
        } finally{
            setIsLoadingTemplate(null);
        }
    };
    const closeError = useCallback(()=>setError(null), []);
    const isInitialLoad = isLoadingTemplateList && !templates.length;
    return /*#__PURE__*/ React.createElement(WorkflowWrapper, {
        onBack: onExit,
        barEnd: /*#__PURE__*/ React.createElement(IconButton, {
            size: "sm",
            "aria-label": t("workflows.TemplateLibrary.refresh"),
            onClick: refreshTemplates,
            disabled: isLoadingTemplateList
        }, isLoadingTemplateList ? /*#__PURE__*/ React.createElement(Spinner, null) : /*#__PURE__*/ React.createElement(RefreshIcon, null))
    }, /*#__PURE__*/ React.createElement(PageScroll, null, /*#__PURE__*/ React.createElement(PageTitle, {
        title: t("workflows.TemplateLibrary.title"),
        lede: t("workflows.TemplateLibrary.lede")
    }), hasLoadError && templates.length > 0 && /*#__PURE__*/ React.createElement(Banner, {
        tone: "error",
        action: /*#__PURE__*/ React.createElement(TextButton, {
            onClick: refreshTemplates
        }, t("generic.retry"))
    }, t("workflows.TemplateLibrary.loadFailed")), hasLoadError && templates.length === 0 && !isLoadingTemplateList && /*#__PURE__*/ React.createElement(EmptyState, {
        icon: /*#__PURE__*/ React.createElement(ErrorIcon, null),
        tone: "neutral",
        text: t("workflows.TemplateLibrary.loadFailed"),
        actions: /*#__PURE__*/ React.createElement(Button, {
            variant: "tonal",
            size: "lg",
            onClick: refreshTemplates
        }, t("generic.retry"))
    }), templates.length > 0 && /*#__PURE__*/ React.createElement(SearchField, {
        value: searchQuery,
        onChange: setSearchQuery,
        placeholder: t("workflows.TemplateLibrary.search")
    }), isInitialLoad && /*#__PURE__*/ React.createElement(CenteredSpinner, null, /*#__PURE__*/ React.createElement(Spinner, {
        size: "lg"
    })), filteredTemplates.length > 0 && /*#__PURE__*/ React.createElement(GroupedList, null, filteredTemplates.map((template)=>/*#__PURE__*/ React.createElement(ListRow, {
            key: template.id,
            icon: /*#__PURE__*/ React.createElement(DocumentIcon, null),
            title: template.title,
            trailing: /*#__PURE__*/ React.createElement(Button, {
                variant: "ghost",
                size: "sm",
                style: {
                    minWidth: "4rem"
                },
                loading: isLoadingTemplate === template.id,
                "aria-label": `${t("generic.open")} ${template.title}`,
                onClick: ()=>openDocument(template.id)
            }, isLoadingTemplate !== template.id && t("generic.open"))
        }))), !isLoadingTemplateList && !hasLoadError && templates.length === 0 && /*#__PURE__*/ React.createElement(EmptyState, {
        icon: /*#__PURE__*/ React.createElement(DocumentIcon, null),
        title: t("workflows.TemplateLibrary.noTemplates")
    }), templates.length > 0 && filteredTemplates.length === 0 && /*#__PURE__*/ React.createElement(EmptyState, {
        icon: /*#__PURE__*/ React.createElement(SearchIcon, null),
        title: t("workflows.noMatches")
    })), /*#__PURE__*/ React.createElement(Toast, {
        open: !!error,
        tone: "error",
        message: error?.message,
        action: error ? {
            label: t("generic.retry"),
            onClick: ()=>{
                setError(null);
                error.retry();
            }
        } : undefined,
        onClose: closeError
    }));
}
